import React, { useMemo, useState } from 'react';
import _get from 'lodash/get';
import _trim from 'lodash/trim';
import _isArray from 'lodash/isArray';
import styles from './index.module.scss';

import { useDebounce, useSeriesFetch } from 'utils/hooks';
import { ExceptionConfigTable } from '../../../../../../components';
import { useProductSkuConfig } from '../../context';
import {
  createProductsData,
  createConfiguredSkusTableRowsData
} from '../../methods';
import { CONFIGURED_SKUS_TABLE_HEADERS } from '../../stubs';
import { PRODUCT_SKU_CONFIG_KEYS as DATA_KEYS } from '../../../../../../stubs';

export default function SkusTable({ onOpenModal = () => {} }) {
  const {
    errors,
    formData,
    isEditMode,
    isProcessing,
    onFormDataChange,
    setInitialProducts
  } = useProductSkuConfig();
  const [searchValue, setSearchValue] = useState('');
  const debounceSearchValue = useDebounce(searchValue);

  const searchQuery = _trim(searchValue ? debounceSearchValue : searchValue);
  const error = _get(errors, DATA_KEYS.PRODUCTS, '');
  const products = _get(formData, DATA_KEYS.PRODUCTS, []);
  const productVariantIds = _get(formData, DATA_KEYS.PRODUCT_VARIANTS, []);

  const { isError, isLoading, fetchData } = useSeriesFetch(
    [
      {
        dataKey: 'productVariants',
        apiKey: 'productVaraints.post',
        method: 'POST',
        payload: {
          variant_ids: productVariantIds,
          out_of_stock_variants: true,
          is_deleted: true
        },
        onTransform: data => (_isArray(data) ? data : [])
      },
      {
        dataKey: 'products',
        apiKey: 'products.getAll',
        method: 'POST',
        payload(aggregateData) {
          const productVariants = _get(aggregateData, 'productVariants', []);
          const productIds = productVariants.map(productVariant => {
            return _get(productVariant, 'product.id');
          });
          return { ids: [...new Set(productIds)], page_size: 10000 };
        }
      }
    ],
    {
      loadOnMount: isEditMode,
      errorMessage: 'Unable to load the SKUs at the moment.',
      onTransform: data => createProductsData(data),
      onSuccess: products => setInitialProducts(products)
    }
  );

  const rows = useMemo(() => {
    return createConfiguredSkusTableRowsData(
      products,
      searchQuery,
      onFormDataChange
    );
  }, [products, searchQuery, onFormDataChange]);

  return (
    <ExceptionConfigTable
      rows={rows}
      error={error}
      title="SKUs Included"
      isError={isError}
      isLoading={isLoading}
      isDisabled={isProcessing}
      searchValue={searchValue}
      searchQuery={searchQuery}
      onSearch={setSearchValue}
      headers={CONFIGURED_SKUS_TABLE_HEADERS}
      errorMessage="Unable to fetch the SKUs at the moment. Please try again later."
      editButtonText="Add SKUs"
      inputPlaceholder="Search included product"
      zeroStateTitle="No SKUs included"
      zeroStateHelptext="Once added, the SKUs will be displayed here"
      zeroStateButtonText="Add SKUs"
      onButtonClick={onOpenModal}
      onReload={fetchData}
      className={styles.container}
    />
  );
}
